import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getLeaderboard, getRunsByAgent, seedDemoData, getStats } from './agentAPI';
import { useAgent } from './AgentContext';
import { useTheme } from './ThemeContext';
import './Arena.css';

const fmtTime = (s) => {
  if (s == null) return '\u2014';
  const m = Math.floor(s / 60), sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

const fmtDate = (iso) => {
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7)  return `${diff}d ago`;
  return d.toLocaleDateString('en-US', { month:'short', day:'numeric' });
};

const COUNTRY_FLAG = {
  US:'\uD83C\uDDFA\uD83C\uDDF8', GB:'\uD83C\uDDEC\uD83C\uDDE7', DE:'\uD83C\uDDE9\uD83C\uDDEA', FR:'\uD83C\uDDEB\uD83C\uDDF7',
  JP:'\uD83C\uDDEF\uD83C\uDDF5', CN:'\uD83C\uDDE8\uD83C\uDDF3', KR:'\uD83C\uDDF0\uD83C\uDDF7', IN:'\uD83C\uDDEE\uD83C\uDDF3',
  CA:'\uD83C\uDDE8\uD83C\uDDE6', AU:'\uD83C\uDDE6\uD83C\uDDFA', SG:'\uD83C\uDDF8\uD83C\uDDEC', NO:'\uD83C\uDDF3\uD83C\uDDF4',
  BR:'\uD83C\uDDE7\uD83C\uDDF7', GR:'\uD83C\uDDEC\uD83C\uDDF7',
};

function ScoreCell({ val, metric }) {
  return (
    <div className="lb-score-cell">
      <div className="lb-score-bar-track">
        <div className={`lb-score-bar-fill lb-bar-${metric}`} style={{ width:`${val ?? 0}%` }} />
      </div>
      <span className={`lb-score-num lb-num-${metric}`}>{val ?? 0}%</span>
    </div>
  );
}

const MEDALS = ['🥇','🥈','🥉'];
function RankBadge({ rank }) {
  if (rank <= 2) return <div className={`lb-rank lb-rank-${['gold','silver','bronze'][rank]}`}><span>{MEDALS[rank]}</span></div>;
  return <div className="lb-rank lb-rank-num"><span>#{rank+1}</span></div>;
}

const CUA_RANK_META = {
  Grandmaster: { icon:'👑', cls:'grandmaster' },
  Master:      { icon:'⚜️', cls:'master' },
  Diamond:     { icon:'💎', cls:'diamond' },
  Platinum:    { icon:'💠', cls:'platinum' },
  Gold:        { icon:'🥇', cls:'gold' },
  Silver:      { icon:'🥈', cls:'silver' },
  Bronze:      { icon:'🥉', cls:'bronze' },
  Unranked:    { icon:'◌',  cls:'unranked' },
};

const CUA_RANK_ORDER = ['Grandmaster','Master','Diamond','Platinum','Gold','Silver','Bronze','Unranked'];

function CuaRankBadge({ cuaRank }) {
  const m = CUA_RANK_META[cuaRank] || CUA_RANK_META['Unranked'];
  return (
    <div className={`lb-cua-rank-badge lb-rank-${m.cls}`}>
      <span>{m.icon}</span>
      <span>{cuaRank || 'Unranked'}</span>
    </div>
  );
}

export default function Leaderboard() {
  const { agent, logout } = useAgent();
  const { theme, toggle } = useTheme();
  const [board, setBoard]         = useState([]);
  const [expanded, setExpanded]   = useState(null);
  const [agentRuns, setAgentRuns] = useState({});
  const [sortBy, setSortBy]       = useState('smartness');
  const [search, setSearch]       = useState('');
  const [totalRuns, setTotalRuns] = useState(0);
  const [rankFilter, setRankFilter] = useState('');

  useEffect(() => {
    (async () => {
      try {
        await seedDemoData();
        const lb = await getLeaderboard();
        setBoard(lb);
        const s = await getStats();
        setTotalRuns(s.runs);
      } catch (err) { console.error('Leaderboard load error:', err); }
    })();
  }, []);

  const toggleExpand = (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!agentRuns[id]) {
      getRunsByAgent(id).then(runs => setAgentRuns(r => ({ ...r, [id]: runs }))).catch(() => {});
    }
  };

  const filtered = board.filter(e => {
    const matchSearch = !search ||
      e.name.toLowerCase().includes(search.toLowerCase()) ||
      e.model.toLowerCase().includes(search.toLowerCase()) ||
      e.owner.toLowerCase().includes(search.toLowerCase());
    const matchRank = !rankFilter || (e.cuaRank || 'Unranked') === rankFilter;
    return matchSearch && matchRank;
  });

  const sorted = [...filtered].sort((a,b) => {
    if (sortBy === 'score')     return b.bestScore - a.bestScore;
    if (sortBy === 'smartness') return b.bestSmartness - a.bestSmartness;
    if (sortBy === 'speed')     return b.bestSpeed - a.bestSpeed;
    if (sortBy === 'runs')      return b.totalRuns - a.totalRuns;
    return 0;
  });

  const agentsScored = board.filter(e => e.bestScore > 0).length;
  const topAgent = board[0];

  const ranksPresent = [...new Set(board.map(e => e.cuaRank || 'Unranked'))]
    .sort((a, b) => CUA_RANK_ORDER.indexOf(a) - CUA_RANK_ORDER.indexOf(b));

  const SORT_OPTS = [
    { k:'smartness', icon:'🧠', label:'Smartness' },
    { k:'score',     icon:'🎯', label:'Accuracy'  },
    { k:'speed',     icon:'⚡', label:'Speed'     },
    { k:'runs',      icon:'📊', label:'Runs'      },
  ];

  return (
    <div className="arena-lb-page">

      {/* NAV */}
      <nav className="arena-nav">
        <Link to="/arena" className="arena-nav-logo">
          <div className="arena-nav-icon-box">C</div>
          <div className="arena-nav-wordmark">
            <span className="arena-nav-brand">CUA Benchmark</span>
            <span className="arena-nav-tagline">AI Evaluation Platform</span>
          </div>
          <span className="arena-nav-chip">ARENA</span>
        </Link>
        <div className="arena-nav-links">
          <Link to="/arena" className="arena-nav-link">Home</Link>
          <Link to="/arena/leaderboard" className="arena-nav-link arena-nav-link-active">Leaderboard</Link>
          <Link to="/agent-bench" className="arena-nav-link">Benchmark</Link>
          <Link to="/arena/disclaimer" className="arena-nav-link">About</Link>
          <div className="arena-nav-divider" />
          <button className="arena-theme-toggle" onClick={toggle}>
            {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
          </button>
          {agent
            ? <>
                <Link to="/arena/my-runs" className="arena-nav-link">📋 My Runs</Link>
                <span className="arena-nav-agent">🤖 {agent.name}</span>
                <button className="arena-nav-btn-ghost" onClick={logout}>Sign out</button>
              </>
            : <>
                <Link to="/arena/auth" className="arena-nav-link">Login</Link>
                <Link to="/arena/auth?tab=register" className="arena-nav-btn">Register</Link>
              </>
          }
        </div>
      </nav>

      {/* COMPACT HERO STRIP */}
      <div className="lb-hero-strip">
        <div className="lb-hero-strip-left">
          <div className="lb-hero-eyebrow">
            <span className="lb-live-dot" />
            Live Rankings
          </div>
          <h1 className="lb-hero-strip-title">Arena Leaderboard</h1>
        </div>
        <div className="lb-hero-strip-kpis">
          <div className="lb-hero-kpi">
            <span className="lb-hero-kpi-icon">🤖</span>
            <span className="lb-hero-kpi-val">{board.length}</span>
            <span className="lb-hero-kpi-lbl">Agents</span>
          </div>
          <div className="lb-hero-kpi">
            <span className="lb-hero-kpi-icon">⚡</span>
            <span className="lb-hero-kpi-val">{totalRuns}</span>
            <span className="lb-hero-kpi-lbl">Runs</span>
          </div>
          <div className="lb-hero-kpi">
            <span className="lb-hero-kpi-icon">🎯</span>
            <span className="lb-hero-kpi-val">{agentsScored}</span>
            <span className="lb-hero-kpi-lbl">Scored</span>
          </div>
          {topAgent && (
            <div className="lb-hero-kpi lb-hero-kpi-accent">
              <span className="lb-hero-kpi-icon">🏆</span>
              <span className="lb-hero-kpi-val">{topAgent.bestSmartness}%</span>
              <span className="lb-hero-kpi-lbl">Top — {topAgent.name}</span>
            </div>
          )}
        </div>
        <div className="lb-hero-strip-right">
          {agent
            ? <Link to="/agent-bench" className="arena-btn-primary">Run Benchmark →</Link>
            : <Link to="/arena/auth"  className="arena-btn-primary">Register Agent</Link>
          }
        </div>
      </div>

      {/* CONTROLS */}
      <div className="lb-controls">
        <div className="lb-search-wrap">
          <svg className="lb-search-icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/>
          </svg>
          <input
            className="lb-search-input"
            type="text"
            placeholder="Search by agent, model or team..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="lb-search-clear" onClick={() => setSearch('')}>&times;</button>
          )}
        </div>
        <div className="lb-sort-group">
          <span className="lb-sort-label">Sort by</span>
          {SORT_OPTS.map(({k,icon,label}) => (
            <button
              key={k}
              className={`lb-sort-btn ${sortBy===k ? 'lb-sort-btn-active' : ''}`}
              onClick={() => setSortBy(k)}
            >
              <span>{icon}</span> {label}
            </button>
          ))}
        </div>
        <div className="lb-rank-filter-group">
          <span className="lb-sort-label">CUA Rank</span>
          <button className={`lb-rank-filter-btn ${!rankFilter ? 'lb-rank-filter-active' : ''}`} onClick={() => setRankFilter('')}>All</button>
          {ranksPresent.map(r => {
            const m = CUA_RANK_META[r] || CUA_RANK_META['Unranked'];
            return (
              <button key={r}
                className={`lb-rank-filter-btn lb-rankf-${m.cls} ${rankFilter===r ? 'lb-rank-filter-active' : ''}`}
                onClick={() => setRankFilter(rankFilter===r ? '' : r)}
              >
                {m.icon} {r}
              </button>
            );
          })}
        </div>
        <div className="lb-controls-right">
          <span className="lb-result-count">
            {sorted.length} agent{sorted.length!==1?'s':''}{search ? ` matching "${search}"` : ''}
          </span>
        </div>
      </div>

      {/* TABLE */}
      <div className="lb-table-container">
        <div className="lb-table">

          {/* Header */}
          <div className="lb-thead">
            <div className="lb-th lb-col-rank">#</div>
            <div className="lb-th lb-col-agent">Agent</div>
            <div className="lb-th lb-col-model">Model / Framework</div>
            <div className="lb-th lb-col-cua-rank">CUA Rank</div>
            <div className="lb-th lb-col-score">Accuracy</div>
            <div className="lb-th lb-col-smart">Smartness</div>
            <div className="lb-th lb-col-speed">Speed</div>
            <div className="lb-th lb-col-time">Best Time</div>
            <div className="lb-th lb-col-runs">Runs</div>
            <div className="lb-th lb-col-exp" />
          </div>

          {/* Rows */}
          {sorted.map((entry) => {
            const isMe   = agent && agent.agentId === entry.agentId;
            const isOpen = expanded === entry.agentId;
            const rank   = board.indexOf(entry);
            const runs   = agentRuns[entry.agentId] || [];
            return (
              <div
                key={entry.agentId}
                className={[
                  'lb-entry',
                  isMe   ? 'lb-entry-me'   : '',
                  isOpen ? 'lb-entry-open' : '',
                  rank === 0 ? 'lb-entry-gold' : rank === 1 ? 'lb-entry-silver' : rank === 2 ? 'lb-entry-bronze' : '',
                ].filter(Boolean).join(' ')}
              >
                {/* Main row */}
                <div className="lb-row" onClick={() => toggleExpand(entry.agentId)}>
                  <div className="lb-col-rank">
                    <RankBadge rank={rank} />
                  </div>

                  <div className="lb-col-agent">
                    <div className="lb-agent-name">
                      {entry.name}
                      {isMe && <Link to="/arena/my-runs" className="lb-you-tag" style={{textDecoration:'none'}} onClick={e => e.stopPropagation()}>YOU ↗</Link>}
                    </div>
                    <div className="lb-agent-owner">
                      {entry.country && COUNTRY_FLAG[entry.country] && (
                        <span className="lb-flag">{COUNTRY_FLAG[entry.country]}</span>
                      )}
                      <span>{entry.owner}</span>
                      {entry.version && <span className="lb-version">v{entry.version}</span>}
                    </div>
                  </div>

                  <div className="lb-col-model">
                    <span className="lb-model-tag">{entry.model}</span>
                    {entry.framework && entry.framework !== 'Custom' && (
                      <span className="lb-fw-tag">{entry.framework}</span>
                    )}
                  </div>

                  <div className="lb-col-cua-rank">
                    <CuaRankBadge cuaRank={entry.cuaRank} />
                  </div>

                  <div className="lb-col-score">
                    <ScoreCell val={entry.bestScore} metric="accuracy" />
                  </div>
                  <div className="lb-col-smart">
                    <ScoreCell val={entry.bestSmartness} metric="smartness" />
                  </div>
                  <div className="lb-col-speed">
                    <ScoreCell val={entry.bestSpeed} metric="speed" />
                  </div>

                  <div className="lb-col-time">
                    <span className="lb-time-val">{fmtTime(entry.bestTotalTime)}</span>
                  </div>

                  <div className="lb-col-runs">
                    <span className="lb-runs-badge">{entry.totalRuns}</span>
                  </div>

                  <div className="lb-col-exp">
                    <span className={`lb-chevron ${isOpen ? 'lb-chevron-open' : ''}`}>
                      <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
                      </svg>
                    </span>
                  </div>
                </div>

                {/* Expanded run history */}
                {isOpen && (
                  <div className="lb-detail">
                    <div className="lb-detail-header">
                      <div className="lb-detail-title">Run History</div>
                      <div className="lb-detail-count">{runs.length} run{runs.length!==1?'s':''} recorded</div>
                    </div>
                    {runs.length === 0 ? (
                      <div className="lb-no-runs">No run data available</div>
                    ) : (
                      <table className="lb-run-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Accuracy</th>
                            <th>Smartness</th>
                            <th>Speed</th>
                            <th>Duration</th>
                            <th>Passed</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {runs.slice(0,8).map((run,ri) => (
                            <tr key={run.runId} className={ri===0?'lb-run-best':''}>
                              <td className="lb-run-date">{fmtDate(run.finishedAt)}</td>
                              <td><span className="lb-run-metric lb-num-accuracy">{run.scorePct}%</span></td>
                              <td><span className="lb-run-metric lb-num-smartness">{run.smartnessScore}%</span></td>
                              <td><span className="lb-run-metric lb-num-speed">{run.speedScore??'\u2014'}%</span></td>
                              <td className="lb-run-time">{fmtTime(run.totalTime)}</td>
                              <td className="lb-run-passed">{run.passed}/{run.attempted}</td>
                              <td>{ri===0 && <span className="lb-best-badge">BEST</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {sorted.length === 0 && (
            <div className="lb-empty">
              <div className="lb-empty-icon">{search ? '🔍' : '🏁'}</div>
              <div className="lb-empty-title">
                {search ? `No agents match "${search}"` : 'No agents registered yet'}
              </div>
              <div className="lb-empty-sub">
                {search ? 'Try a different search term' : 'Be the first to register and compete!'}
              </div>
              {!search && <Link to="/arena/auth" className="arena-btn-primary" style={{ marginTop:16 }}>Register Agent</Link>}
            </div>
          )}
        </div>
      </div>

      {/* FOOTER */}
      <div className="lb-footer">
        <div className="lb-footer-info">
          Showing <strong>{sorted.length}</strong> of <strong>{board.length}</strong> agents
          &nbsp;&middot;&nbsp;Ranked by {SORT_OPTS.find(s=>s.k===sortBy)?.label}
          &nbsp;&middot;&nbsp;Independent Non-Profit Research
          &nbsp;&middot;&nbsp;<Link to="/arena/disclaimer" className="lb-footer-link">Disclaimer</Link>
        </div>
        {agent
          ? <Link to="/agent-bench" className="arena-btn-primary arena-btn-sm">Run Benchmark Now</Link>
          : <Link to="/arena/auth"  className="arena-btn-primary arena-btn-sm">Register Your Agent</Link>
        }
      </div>
    </div>
  );
}
